
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { ShopSettingsSidebar } from "@/components/dashboard/shops/settings/ShopSettingsSidebar";
import { ProfileSection } from "@/components/dashboard/shops/settings/profile-section";
import { FormMenuSection } from "@/components/dashboard/shops/settings/FormMenuSection";
import { SocialMediaSection } from "@/components/dashboard/shops/settings/SocialMediaSection";
import { AnnouncementBarSection } from "@/components/dashboard/shops/settings/AnnouncementBarSection";
import { ThankYouPageSection } from "@/components/dashboard/shops/settings/ThankYouPageSection";
import { PaymentMethodsSection } from "@/components/dashboard/shops/settings/PaymentMethodsSection";
import { DeleteShopButton } from "@/components/dashboard/shops/settings/DeleteShopButton";
import { CustomDomainsSection } from "@/components/dashboard/shops/settings/CustomDomainsSection";
import { TrackingSettingsSection } from "@/components/dashboard/shops/settings/TrackingSettingsSection";
import { getShops } from "@/lib/supabase/shops";
import { Shop } from "@/components/dashboard/shops/types";
import { Loader, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from "@/components/ui/breadcrumb";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const ShopSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const shopId = location.state?.shopId;
  const initialActiveMenu = location.state?.activeMenu || "profile";
  
  const [activeMenu, setActiveMenu] = useState<string>(initialActiveMenu);
  const [shop, setShop] = useState<Shop | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  useEffect(() => {
    if (!shopId) {
      toast({
        title: "Erreur",
        description: "Identifiant de boutique manquant",
        variant: "destructive",
      });
      navigate("/dashboard");
      return;
    }
    
    const fetchShopData = async () => {
      try {
        setIsLoading(true);
        const shops = await getShops();
        const shopData = shops.find(s => s.id === shopId);
        
        if (!shopData) {
          toast({
            title: "Erreur",
            description: "Boutique non trouvée",
            variant: "destructive",
          });
          navigate("/dashboard");
          return;
        }
        
        setShop(shopData);
      } catch (error) {
        console.error("Erreur lors de la récupération des données de la boutique:", error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les données de la boutique",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchShopData();
  }, [shopId, navigate, toast]);

  const handleGoBack = () => {
    navigate(-1);
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">
        <Loader className="h-10 w-10 animate-spin text-custom-primary" />
      </div>
    );
  }
  
  if (!shop) {
    return (
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-4">Boutique non trouvée</h1>
        <p>La boutique que vous recherchez n'existe pas ou vous n'y avez pas accès.</p>
      </div>
    );
  }

  const renderActiveSection = () => {
    switch (activeMenu) {
      case "profile":
        return <ProfileSection shop={shop} />;
      case "forms":
        return <FormMenuSection shop={shop} />;
      case "social-media":
        return <SocialMediaSection shop={shop} />;
      case "announcement-bar":
        return <AnnouncementBarSection shop={shop} />;
      case "thank-you-page":
        return <ThankYouPageSection shop={shop} />;
      case "payment":
        return <PaymentMethodsSection shop={shop} />;
      case "domains":
        return <CustomDomainsSection shop={shop} />;
      case "tracking":
        return <TrackingSettingsSection shop={shop} />;
      default:
        return <ProfileSection shop={shop} />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in dark:text-gray-100 transition-colors bg-pastel-blue-light dark:bg-[#121624]">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between mb-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleGoBack}
            className="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
          
          <Breadcrumb className="hidden md:flex">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{shop?.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold mb-2 text-gray-800 dark:text-gray-100">Paramètres De La Boutique</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Gérez les paramètres et les informations de votre boutique
            </p>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
        <ShopSettingsSidebar 
          shop={shop}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
        />
        
        {renderActiveSection()}
      </div>

      <Card className="mt-12 border-red-200 dark:border-red-900/30 p-6">
        <div className="flex flex-col space-y-4">
          <Separator className="bg-red-200 dark:bg-red-800/30" />
          
          <div className="py-2">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              La suppression de cette boutique est une action irréversible. Toutes les données associées 
              seront définitivement perdues, y compris les produits, commandes et configurations.
            </p>
            
            <div className="flex justify-end">
              <DeleteShopButton shop={shop} />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ShopSettings;
